import type { ConceptEdge } from '@arianna/shared'
import { relationFamily } from './families.js'
import type { ChapterGraph } from './types.js'

export interface FocusState {
  focusNodeId: string
  /** Archi intra + cross incidenti al fuoco. */
  hotEdgeIds: Set<string>
  /** Concetti residenti collegati al fuoco. */
  neighborIds: Set<string>
  /** Esterni distinti (un nodo per esterno, con l'arco più confidente), ordinati per confidence desc, edgeId asc. */
  externals: Array<{ node: ChapterGraph['externalNodes'][number]; edge: ConceptEdge }>
  /** Tutti i vicini per la raggiera: residenti (confidence desc, edgeId asc) poi esterni (stesso ordine). */
  neighbors: Array<{ nodeId: string; external: boolean; edge: ConceptEdge }>
}

function byConfidenceDescThenEdgeIdAsc(a: ConceptEdge, b: ConceptEdge): number {
  if (a.confidence !== b.confidence) return b.confidence - a.confidence
  return a.edgeId < b.edgeId ? -1 : a.edgeId > b.edgeId ? 1 : 0
}

export function computeFocus(graph: ChapterGraph, focusNodeId: string): FocusState {
  const nodeIds = new Set(graph.nodes.map((n) => n.nodeId))
  const externalById = new Map(graph.externalNodes.map((n) => [n.nodeId, n]))

  const hotEdgeIds = new Set<string>()
  const neighborIds = new Set<string>()
  const bestResidentEdge = new Map<string, ConceptEdge>()
  const bestExternalEdge = new Map<string, ConceptEdge>()

  for (const e of graph.edges) {
    if (relationFamily(e.relationType) === null) continue
    const incident = e.sourceNodeId === focusNodeId || e.targetNodeId === focusNodeId
    if (!incident) continue

    hotEdgeIds.add(e.edgeId)
    const otherId = e.sourceNodeId === focusNodeId ? e.targetNodeId : e.sourceNodeId

    if (nodeIds.has(otherId)) {
      neighborIds.add(otherId)
      const cur = bestResidentEdge.get(otherId)
      if (!cur || byConfidenceDescThenEdgeIdAsc(e, cur) < 0) bestResidentEdge.set(otherId, e)
    } else if (externalById.has(otherId)) {
      const current = bestExternalEdge.get(otherId)
      if (!current || byConfidenceDescThenEdgeIdAsc(e, current) < 0) {
        bestExternalEdge.set(otherId, e)
      }
    }
  }

  const externals = [...bestExternalEdge.entries()]
    .map(([otherId, edge]) => ({ node: externalById.get(otherId)!, edge }))
    .sort((a, b) => byConfidenceDescThenEdgeIdAsc(a.edge, b.edge))

  const residents = [...bestResidentEdge.entries()]
    .map(([nodeId, edge]) => ({ nodeId, external: false, edge }))
    .sort((a, b) => byConfidenceDescThenEdgeIdAsc(a.edge, b.edge))
  const neighbors = [
    ...residents,
    ...externals.map((x) => ({ nodeId: x.node.nodeId, external: true, edge: x.edge })),
  ]

  return { focusNodeId, hotEdgeIds, neighborIds, externals, neighbors }
}

/** Per nodo residente: numero di archi cross incidenti. */
export function crossBadgeCounts(graph: ChapterGraph): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const n of graph.nodes) counts[n.nodeId] = 0
  for (const e of graph.edges) {
    if (e.scope !== 'cross') continue
    if (relationFamily(e.relationType) === null) continue
    const sourceCount = counts[e.sourceNodeId]
    if (sourceCount !== undefined) counts[e.sourceNodeId] = sourceCount + 1
    const targetCount = counts[e.targetNodeId]
    if (targetCount !== undefined) counts[e.targetNodeId] = targetCount + 1
  }
  return counts
}
